import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf } from './cmsTheme.ts';
import { LineupNames } from '../LineupNames.tsx';
import { useFestivalData } from '../../lib/festivalData.ts';
import { getArtistGroups } from '../../application/festivalSelectors.ts';
import type { ProgramSection } from '../../lib/types.ts';

/**
 * "Navnene på plakaten" names list (CMS-039), system-fed from already-published festival data
 * (DataContext) — the section carries no authored body. Renders the announced artists and/or the
 * features/"Oplevelser" as a flowing backslash-separated run (shared LineupNames, same as the hero
 * and the line-up strip). `list` picks which list(s) to show; 'both' shows them as two separate
 * labelled blocks. Distinct from the Program page artists grid (`CmsArtistsSection`).
 *
 * v2 sizes: the names are the content, so they sit at 30/44px — larger than body copy, smaller
 * than the old poster scale that made a names list read as a second hero. The features run below
 * them at 20/24px in muted ink, which is what makes it read as supplementary.
 */
export function CmsProgramSection({ theme, guitar, list = 'artists', featuresHeading }: ProgramSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const groups = data ? getArtistGroups(data) : null;

  const showArtists = list === 'artists' || list === 'both';
  // Features are supplementary: rendered as their own block only when selected on their own; in
  // 'both' an empty features list is hidden rather than showing a "annonceres snart" placeholder.
  const renderFeatures =
    (list === 'features') || (list === 'both' && (groups?.features.length ?? 0) > 0);

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser navne…"
        bodyClass={t.body}
        errorMessage="Navnene kunne ikke indlæses."
      >
        {groups && (
          <div className="space-y-10 md:space-y-14">
            {showArtists && (
              <div>
                <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>
                  Navnene på plakaten
                </SectionHeading>
                <LineupNames
                  items={groups.artists.map(a => ({ name: a.name, id: a.id }))}
                  className={`font-display text-[30px] md:text-[44px] leading-[1.05] tracking-[-0.02em] ${t.heading}`}
                  emptyLabel="Årets navne annonceres snart"
                />
              </div>
            )}
            {renderFeatures && (
              <div>
                <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>
                  {featuresHeading || 'Midtfynske oplevelser'}
                </SectionHeading>
                <LineupNames
                  items={groups.features.map(a => ({ name: a.name, id: a.id }))}
                  className={`font-display text-xl md:text-2xl leading-[1.3] tracking-[-0.01em] ${t.body}`}
                  emptyLabel="Oplevelser annonceres snart"
                />
              </div>
            )}
          </div>
        )}
      </CmsSectionState>
    </CmsSectionShell>
  );
}
